import React, { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { toast } from "sonner";
import { Pencil, Trash, Plus, Upload, X, Star, ArrowUp, ArrowDown } from "@phosphor-icons/react";
import { formatApiError } from "@/contexts/AuthContext";

const EMPTY = {
  name: "", slug: "", short_description: "", description: "",
  category: "", subcategory: "", brand: "",
  supplier_price: 0, images: [""], specifications: {},
  stock: 0, condition: "new",
  wheel_size: "", frame_size: "", color: "", motor_brand: "", battery_capacity: "", suspension_travel: "",
  featured: false, best_seller: false, new_arrival: false,
  variant_options: [], variants: [],
};

function slugify(s) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

/**
 * VariantsEditor — inline table of purchasable variants for a product.
 * Optional: leave empty axes+variants and the product behaves exactly like
 * before (single SKU using top-level price/stock).
 */
function VariantsEditor({ axes, variants, supplierBase, onAxesChange, onVariantsChange }) {
  const addAxis = () => {
    const name = window.prompt("Option name (e.g. Length, Size, Color)");
    if (!name) return;
    if (axes.includes(name)) { toast.error("Axis already exists"); return; }
    onAxesChange([...axes, name]);
    // Extend every existing variant with an empty value for the new axis
    onVariantsChange((variants || []).map((v) => ({ ...v, options: { ...v.options, [name]: "" } })));
  };
  const removeAxis = (axis) => {
    if (!window.confirm(`Remove axis "${axis}"? Every variant loses that field.`)) return;
    onAxesChange(axes.filter((a) => a !== axis));
    onVariantsChange((variants || []).map((v) => {
      const { [axis]: _drop, ...rest } = v.options || {};
      return { ...v, options: rest };
    }));
  };
  const addVariant = () => {
    const suggested = Math.round((parseFloat(supplierBase) || 0) * 1.30 * 100) / 100;
    onVariantsChange([
      ...(variants || []),
      { sku: "", options: Object.fromEntries(axes.map((a) => [a, ""])), supplier_price: parseFloat(supplierBase) || 0, price: suggested || "", stock: 0, image: "" },
    ]);
  };
  const updVariant = (i, patch) => {
    const copy = [...variants];
    copy[i] = { ...copy[i], ...patch };
    onVariantsChange(copy);
  };
  const updVariantOpt = (i, axis, value) => {
    const copy = [...variants];
    copy[i] = { ...copy[i], options: { ...copy[i].options, [axis]: value } };
    onVariantsChange(copy);
  };
  const rmVariant = (i) => onVariantsChange(variants.filter((_, k) => k !== i));
  const applyMarkup = (i) => {
    const v = variants[i];
    const sup = parseFloat(v.supplier_price) || 0;
    updVariant(i, { price: Math.round(sup * 1.30 * 100) / 100 });
  };

  return (
    <div className="border border-neutral-200 p-4 bg-neutral-50" data-testid="variants-editor">
      <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="overline text-neutral-600 mb-1">Variants / Options</div>
          <p className="text-[11px] text-neutral-500 max-w-md">Optional. Leave empty for a single-SKU product. Add axes (e.g. Length) then add variant rows — each row has its own price and stock.</p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={addAxis} className="vt-btn-outline text-[11px]" data-testid="variant-add-axis">+ Axis</button>
          <button type="button" onClick={addVariant} disabled={axes.length === 0} className="vt-btn-orange text-[11px]" data-testid="variant-add-row">+ Add Variant</button>
        </div>
      </div>
      {axes.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {axes.map((a) => (
            <span key={a} className="inline-flex items-center gap-1 bg-white border border-neutral-300 pl-2 pr-1 py-0.5 text-xs">
              <span className="font-bold">{a}</span>
              <button type="button" onClick={() => removeAxis(a)} className="opacity-50 hover:opacity-100 hover:text-red-600" title="Remove axis"><X size={10} weight="bold" /></button>
            </span>
          ))}
        </div>
      )}
      {(variants || []).length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="variants-table">
            <thead className="bg-white border border-neutral-200">
              <tr>
                {axes.map((a) => <th key={a} className="text-left p-2">{a}</th>)}
                <th className="text-left p-2">SKU</th>
                <th className="text-right p-2">Supplier €</th>
                <th className="text-right p-2">Retail €</th>
                <th className="text-right p-2">Stock</th>
                <th className="text-left p-2">Image URL</th>
                <th className="p-2 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {variants.map((v, i) => (
                <tr key={i} className="border-b border-neutral-200 bg-white" data-testid={`variant-row-${i}`}>
                  {axes.map((a) => (
                    <td key={a} className="p-1"><input className="vt-input py-1 text-xs" value={v.options?.[a] || ""} onChange={(e) => updVariantOpt(i, a, e.target.value)} data-testid={`variant-${i}-opt-${a}`} /></td>
                  ))}
                  <td className="p-1"><input className="vt-input py-1 text-xs" value={v.sku || ""} onChange={(e) => updVariant(i, { sku: e.target.value })} data-testid={`variant-${i}-sku`} /></td>
                  <td className="p-1"><input type="number" step="0.01" className="vt-input py-1 text-xs text-right" value={v.supplier_price ?? ""} onChange={(e) => updVariant(i, { supplier_price: e.target.value })} data-testid={`variant-${i}-supplier`} /></td>
                  <td className="p-1 whitespace-nowrap">
                    <input type="number" step="0.01" className="vt-input py-1 text-xs text-right inline-block w-24" value={v.price ?? ""} placeholder="auto +30%" onChange={(e) => updVariant(i, { price: e.target.value })} data-testid={`variant-${i}-price`} />
                    <button type="button" onClick={() => applyMarkup(i)} className="ml-1 text-[10px] text-[color:var(--vt-orange)] hover:underline" title="Apply +30% to supplier">+30%</button>
                  </td>
                  <td className="p-1"><input type="number" className="vt-input py-1 text-xs text-right w-16" value={v.stock ?? 0} onChange={(e) => updVariant(i, { stock: e.target.value })} data-testid={`variant-${i}-stock`} /></td>
                  <td className="p-1"><input className="vt-input py-1 text-xs" value={v.image || ""} placeholder="/api/uploads/... or https://…" onChange={(e) => updVariant(i, { image: e.target.value })} data-testid={`variant-${i}-image`} /></td>
                  <td className="p-1"><button type="button" onClick={() => rmVariant(i)} className="p-1 text-red-600 hover:bg-red-50" title="Remove variant" data-testid={`variant-${i}-remove`}><Trash size={12} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-[11px] text-neutral-500 italic">No variants yet. {axes.length === 0 ? "Add an axis first." : "Click + Add Variant."}</p>
      )}
    </div>
  );
}

function ProductModal({ product, categories, onClose, onSaved }) {
  const [form, setForm] = useState(product ? {
    ...EMPTY, ...product,
    images: product.images?.length ? product.images : [""],
    specifications: product.specifications || {},
  } : { ...EMPTY, category: categories[0]?.key || "" });
  const [specRows, setSpecRows] = useState(
    Object.entries(product?.specifications || {}).map(([k, v]) => ({ k, v: String(v) }))
  );
  const [saving, setSaving] = useState(false);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const specifications = {};
      specRows.forEach((r) => { if (r.k) specifications[r.k] = r.v; });
      const payload = {
        ...form,
        supplier_price: parseFloat(form.supplier_price) || 0,
        stock: parseInt(form.stock) || 0,
        specifications,
        images: form.images.filter(Boolean),
        slug: form.slug || slugify(form.name),
      };
      if (product?.id) {
        await api.put(`/products/${product.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product created");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const retail = ((parseFloat(form.supplier_price) || 0) * 1.30).toFixed(2);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="product-modal">
      <div className="bg-white w-full max-w-3xl my-8 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white flex items-center justify-between p-6 border-b border-neutral-200 z-10">
          <h2 className="font-display font-black text-2xl">{product ? "Edit Product" : "New Product"}</h2>
          <button onClick={onClose} data-testid="modal-close" className="hover:text-[color:var(--vt-orange)]"><X size={22} /></button>
        </div>
        <form onSubmit={save} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="overline text-neutral-500 block mb-1">Name*</label>
              <input required className="vt-input" value={form.name} onChange={(e) => { upd("name", e.target.value); if (!product) upd("slug", slugify(e.target.value)); }} data-testid="product-name" />
            </div>
            <div className="col-span-2">
              <label className="overline text-neutral-500 block mb-1">Slug*</label>
              <input required className="vt-input" value={form.slug} onChange={(e) => upd("slug", e.target.value)} data-testid="product-slug" />
            </div>
            <div>
              <label className="overline text-neutral-500 block mb-1">Category*</label>
              <select required className="vt-input" value={form.category} onChange={(e) => { upd("category", e.target.value); upd("subcategory", ""); }} data-testid="product-category">
                <option value="">— select —</option>
                {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
              {categories.length === 0 && (
                <p className="text-[11px] text-red-600 mt-1">No categories yet. Create one under Admin → Categories first.</p>
              )}
            </div>
            <div>
              <label className="overline text-neutral-500 block mb-1">Subcategory</label>
              <select className="vt-input" value={form.subcategory} onChange={(e) => upd("subcategory", e.target.value)} data-testid="product-subcategory">
                <option value="">—</option>
                {(categories.find((c) => c.key === form.category)?.subcategories || []).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="overline text-neutral-500 block mb-1">Brand</label>
              <input className="vt-input" value={form.brand} onChange={(e) => upd("brand", e.target.value)} />
            </div>
            <div>
              <label className="overline text-neutral-500 block mb-1">Condition</label>
              <select className="vt-input" value={form.condition} onChange={(e) => upd("condition", e.target.value)}>
                <option value="new">new</option><option value="used">used</option>
              </select>
            </div>
            <div>
              <label className="overline text-neutral-500 block mb-1">Supplier Price (€)*</label>
              <input required type="number" step="0.01" className="vt-input" value={form.supplier_price} onChange={(e) => upd("supplier_price", e.target.value)} data-testid="product-supplier-price" />
            </div>
            <div>
              <label className="overline text-neutral-500 block mb-1">Retail (30% markup)</label>
              <div className="vt-input bg-neutral-100 font-bold text-[color:var(--vt-orange)]" data-testid="product-retail-price">€{retail}</div>
            </div>
            <div>
              <label className="overline text-neutral-500 block mb-1">Stock*</label>
              <input required type="number" className="vt-input" value={form.stock} onChange={(e) => upd("stock", e.target.value)} />
            </div>
            <div>
              <label className="overline text-neutral-500 block mb-1">Wheel size</label>
              <input className="vt-input" value={form.wheel_size} onChange={(e) => upd("wheel_size", e.target.value)} />
            </div>
            <div>
              <label className="overline text-neutral-500 block mb-1">Frame size</label>
              <input className="vt-input" value={form.frame_size} onChange={(e) => upd("frame_size", e.target.value)} />
            </div>
            <div>
              <label className="overline text-neutral-500 block mb-1">Color</label>
              <input className="vt-input" value={form.color} onChange={(e) => upd("color", e.target.value)} />
            </div>
            <div>
              <label className="overline text-neutral-500 block mb-1">Motor brand</label>
              <input className="vt-input" value={form.motor_brand} onChange={(e) => upd("motor_brand", e.target.value)} />
            </div>
            <div>
              <label className="overline text-neutral-500 block mb-1">Battery capacity</label>
              <input className="vt-input" value={form.battery_capacity} onChange={(e) => upd("battery_capacity", e.target.value)} />
            </div>
            <div>
              <label className="overline text-neutral-500 block mb-1">Suspension travel</label>
              <input className="vt-input" value={form.suspension_travel} onChange={(e) => upd("suspension_travel", e.target.value)} />
            </div>
          </div>

          <div>
            <label className="overline text-neutral-500 block mb-1">Short description</label>
            <input className="vt-input" value={form.short_description} onChange={(e) => upd("short_description", e.target.value)} />
          </div>
          <div>
            <label className="overline text-neutral-500 block mb-1">Description</label>
            <textarea rows="4" className="vt-input" value={form.description} onChange={(e) => upd("description", e.target.value)} />
          </div>

          {form.source_url !== undefined && (
            <div>
              <label className="overline text-neutral-500 block mb-1">
                Source Product URL
                {form.source_domain && <span className="ml-2 text-[10px] text-neutral-400 normal-case">(scraped from {form.source_domain})</span>}
              </label>
              <div className="flex gap-2">
                <input
                  className="vt-input flex-1 font-mono text-[11px]"
                  value={form.source_url || ""}
                  onChange={(e) => upd("source_url", e.target.value)}
                  placeholder="https://supplier.com/product/..."
                  data-testid="product-source-url"
                />
                {form.source_url && (
                  <a href={form.source_url} target="_blank" rel="noopener noreferrer"
                    className="vt-btn-outline text-[11px] whitespace-nowrap" data-testid="product-source-open">
                    Open ↗
                  </a>
                )}
              </div>
              <p className="text-[10px] text-neutral-500 mt-1">Kept in sync with the daily 04:00 UTC price/stock refresh. Change only if the supplier moved the product.</p>
            </div>
          )}

          <div>
            <label className="overline text-neutral-500 block mb-1">Images ({form.images.filter(Boolean).length})</label>
            <p className="text-[11px] text-neutral-500 mb-2">First image is used as the main / cover on the storefront. Click <Star size={10} className="inline mb-[2px]" weight="fill" /> to promote another image to main.</p>

            {/* Preview grid — dedup empty slot, show previews with actions */}
            <div className="flex flex-wrap gap-2 mb-3">
              {form.images.filter(Boolean).map((img, i, arr) => (
                <div key={i} className={`relative group border ${i === 0 ? "border-[color:var(--vt-orange)] border-2" : "border-neutral-200"}`} data-testid={`product-image-${i}`}>
                  <img src={img} alt="" className="w-24 h-24 object-cover" onError={(e) => { e.target.style.opacity = 0.3; }} />
                  {i === 0 && <span className="absolute top-0 left-0 bg-[color:var(--vt-orange)] text-black text-[9px] uppercase font-bold px-1.5 py-0.5">MAIN</span>}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1">
                    {i !== 0 && (
                      <button type="button" title="Set as main" onClick={() => {
                        const kept = form.images.filter(Boolean);
                        const promoted = [kept[i], ...kept.filter((_, k) => k !== i)];
                        upd("images", promoted);
                      }} className="text-white text-[10px] uppercase font-bold flex items-center gap-1" data-testid={`img-main-${i}`}>
                        <Star size={12} weight="fill" /> Main
                      </button>
                    )}
                    <div className="flex gap-1">
                      <button type="button" disabled={i === 0} onClick={() => {
                        const kept = form.images.filter(Boolean);
                        [kept[i - 1], kept[i]] = [kept[i], kept[i - 1]];
                        upd("images", kept);
                      }} className="p-1 bg-white/20 text-white disabled:opacity-30" data-testid={`img-up-${i}`}><ArrowUp size={10} /></button>
                      <button type="button" disabled={i === arr.length - 1} onClick={() => {
                        const kept = form.images.filter(Boolean);
                        [kept[i + 1], kept[i]] = [kept[i], kept[i + 1]];
                        upd("images", kept);
                      }} className="p-1 bg-white/20 text-white disabled:opacity-30" data-testid={`img-down-${i}`}><ArrowDown size={10} /></button>
                      <button type="button" onClick={() => {
                        const next = form.images.filter(Boolean).filter((_, k) => k !== i);
                        upd("images", next.length ? next : [""]);
                      }} className="p-1 bg-red-600 text-white" title="Remove" data-testid={`img-remove-${i}`}><Trash size={10} /></button>
                    </div>
                  </div>
                </div>
              ))}
              {form.images.filter(Boolean).length === 0 && (
                <div className="w-24 h-24 border border-dashed border-neutral-300 flex items-center justify-center text-neutral-400 text-[10px] text-center px-2">No images yet</div>
              )}
            </div>

            {/* Upload from computer — reuses /api/admin/uploads/image (JPG/PNG/WEBP, 3MB per file) */}
            <div className="flex flex-wrap gap-2">
              <label className="text-xs font-bold uppercase tracking-widest border border-black hover:bg-black hover:text-white px-3 py-2 cursor-pointer transition-colors flex items-center gap-1" data-testid="product-upload-btn">
                <Upload size={12} />
                <span>Upload from computer</span>
                <input type="file" accept="image/jpeg,image/jpg,image/png,image/webp" multiple className="hidden" onChange={async (e) => {
                  const files = Array.from(e.target.files || []);
                  if (!files.length) return;
                  try {
                    const uploaded = [];
                    for (const file of files) {
                      const fd = new FormData();
                      fd.append("file", file);
                      const { data } = await api.post("/admin/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
                      uploaded.push(data.url);
                    }
                    upd("images", [...form.images.filter(Boolean), ...uploaded]);
                    toast.success(`${uploaded.length} image${uploaded.length === 1 ? "" : "s"} uploaded`);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Upload failed (JPG/PNG/WEBP, max 8MB each)");
                  } finally { e.target.value = ""; }
                }} />
              </label>
              <button type="button" onClick={() => {
                const url = window.prompt("Paste image URL:");
                if (url && url.trim()) upd("images", [...form.images.filter(Boolean), url.trim()]);
              }} className="text-xs font-bold uppercase tracking-widest border border-neutral-300 hover:border-black px-3 py-2 transition-colors" data-testid="product-url-btn">
                + Add URL
              </button>
            </div>
          </div>

          <div>
            <label className="overline text-neutral-500 block mb-1">Specifications</label>
            {specRows.map((row, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 mb-2">
                <input className="vt-input" placeholder="Key" value={row.k} onChange={(e) => {
                  const nr = [...specRows]; nr[i] = { ...nr[i], k: e.target.value }; setSpecRows(nr);
                }} />
                <input className="vt-input" placeholder="Value" value={row.v} onChange={(e) => {
                  const nr = [...specRows]; nr[i] = { ...nr[i], v: e.target.value }; setSpecRows(nr);
                }} />
                <button type="button" onClick={() => setSpecRows(specRows.filter((_, idx) => idx !== i))} className="text-neutral-400 hover:text-red-600 px-2"><Trash size={16} /></button>
              </div>
            ))}
            <button type="button" onClick={() => setSpecRows([...specRows, { k: "", v: "" }])} className="text-xs font-bold text-[color:var(--vt-orange)]">+ Add spec</button>
          </div>

          <div className="flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.featured} onChange={(e) => upd("featured", e.target.checked)} /> Featured</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.best_seller} onChange={(e) => upd("best_seller", e.target.checked)} /> Best Seller</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.new_arrival} onChange={(e) => upd("new_arrival", e.target.checked)} /> New Arrival</label>
          </div>

          <VariantsEditor
            axes={form.variant_options || []}
            variants={form.variants || []}
            supplierBase={form.supplier_price}
            onAxesChange={(next) => upd("variant_options", next)}
            onVariantsChange={(next) => upd("variants", next)}
          />

          <div className="pt-4 flex gap-2 justify-end sticky bottom-0 bg-white border-t border-neutral-200 -mx-6 px-6 py-4">
            <button type="button" onClick={onClose} className="vt-btn-outline text-xs">Cancel</button>
            <button type="submit" disabled={saving} className="vt-btn-orange text-xs" data-testid="product-save">{saving ? "..." : "Save Product"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function BulkImportModal({ onClose, onDone }) {
  const [csv, setCsv] = useState("");
  const [busy, setBusy] = useState(false);

  const parseCsv = (text) => {
    const lines = text.trim().split(/\r?\n/);
    if (lines.length < 2) return [];
    const headers = lines[0].split(",").map((h) => h.trim());
    return lines.slice(1).map((line) => {
      const cells = line.split(",").map((c) => c.trim());
      const obj = {};
      headers.forEach((h, i) => { obj[h] = cells[i] || ""; });
      if (obj.supplier_price) obj.supplier_price = parseFloat(obj.supplier_price) || 0;
      if (obj.stock) obj.stock = parseInt(obj.stock) || 0;
      if (obj.images && typeof obj.images === "string") obj.images = obj.images.split("|").filter(Boolean);
      if (!obj.images) obj.images = [];
      if (!obj.slug && obj.name) obj.slug = slugify(obj.name);
      if (!obj.condition) obj.condition = "new";
      return obj;
    });
  };

  const doImport = async () => {
    setBusy(true);
    try {
      const products = parseCsv(csv);
      if (products.length === 0) { toast.error("No products parsed"); setBusy(false); return; }
      const { data } = await api.post("/admin/products/bulk", { products });
      toast.success(`Imported: ${data.created} new, ${data.updated} updated`);
      onDone(); onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Import failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="bulk-import-modal">
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-neutral-200">
          <h2 className="font-display font-black text-2xl">Bulk Import (CSV)</h2>
          <button onClick={onClose}><X size={22} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="text-xs text-neutral-500">
            <p className="mb-2 font-bold">Required columns:</p>
            <p className="mb-2"><code>name,slug,category,supplier_price,stock,brand,short_description,images,condition</code></p>
            <p>Images separated by <code>|</code>. Supplier price will auto-mark up 30% for retail.</p>
          </div>
          <textarea
            rows="14"
            className="vt-input font-mono text-xs"
            placeholder={"name,slug,category,supplier_price,stock,brand,short_description,images,condition\nTrek Fuel EX,trek-fuel-ex,new-bikes,3499,5,Trek,Trail bike,https://img.jpg,new"}
            value={csv}
            onChange={(e) => setCsv(e.target.value)}
            data-testid="bulk-csv"
          />
          <div className="flex gap-2 justify-end">
            <button onClick={onClose} className="vt-btn-outline text-xs">Cancel</button>
            <button onClick={doImport} disabled={busy} className="vt-btn-orange text-xs" data-testid="bulk-import-submit">{busy ? "..." : "Import"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showBulk, setShowBulk] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/products").then((r) => setProducts(r.data)),
      api.get("/admin/categories").then((r) => setCategories(r.data)).catch((err) => {
        // Surface the failure — silent [] used to make Category/Subcategory
        // dropdowns look empty even when categories existed. See bugfix 2026-07-24.
        console.error("Failed to load categories:", err);
        toast.error("Couldn't load Categories — Category dropdown will be empty. Retry after refresh.");
        setCategories([]);
      }),
    ]).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const filtered = products.filter((p) =>
    !q || p.name.toLowerCase().includes(q.toLowerCase()) || p.brand?.toLowerCase().includes(q.toLowerCase()) || p.category.includes(q.toLowerCase())
  );

  return (
    <div className="p-8 lg:p-12" data-testid="admin-products">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="overline text-[color:var(--vt-orange)] mb-2">/ CATALOG</div>
          <h1 className="font-display font-black text-4xl tracking-tighter">Products</h1>
          <p className="text-neutral-500 text-sm mt-1">{products.length} products in catalog</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowBulk(true)} className="vt-btn-outline text-xs" data-testid="btn-bulk-import"><Upload size={14} weight="bold" /> Bulk Import</button>
          <button onClick={() => setShowNew(true)} className="vt-btn-orange text-xs" data-testid="btn-new-product"><Plus size={14} weight="bold" /> New Product</button>
        </div>
      </div>

      <div className="mb-4">
        <input className="vt-input max-w-md" placeholder="Search by name, brand, category..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="products-search" />
      </div>

      <div className="bg-white border border-neutral-200 overflow-x-auto">
        {loading ? (
          <div className="p-12 text-center text-neutral-500">Loading...</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr>
                <th className="text-left p-3 overline text-neutral-600">Image</th>
                <th className="text-left p-3 overline text-neutral-600">Name</th>
                <th className="text-left p-3 overline text-neutral-600">Category</th>
                <th className="text-left p-3 overline text-neutral-600">Brand</th>
                <th className="text-right p-3 overline text-neutral-600">Supplier €</th>
                <th className="text-right p-3 overline text-neutral-600">Retail €</th>
                <th className="text-right p-3 overline text-neutral-600">Stock</th>
                <th className="text-right p-3 overline text-neutral-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`product-row-${p.slug}`}>
                  <td className="p-3">
                    <img src={p.images?.[0]} alt="" className="w-12 h-12 object-cover" />
                  </td>
                  <td className="p-3">
                    <div className="font-bold">{p.name}</div>
                    <div className="text-[10px] text-neutral-500">{p.slug}</div>
                  </td>
                  <td className="p-3 text-xs">{p.category}<div className="text-[10px] text-neutral-500">{p.subcategory}</div></td>
                  <td className="p-3 text-xs">{p.brand}</td>
                  <td className="p-3 text-right text-xs text-neutral-500">€{p.supplier_price?.toFixed(2)}</td>
                  <td className="p-3 text-right font-bold text-[color:var(--vt-orange)]">€{p.retail_price?.toFixed(2)}</td>
                  <td className="p-3 text-right">{p.stock}</td>
                  <td className="p-3 text-right">
                    <button onClick={() => setEditing(p)} className="p-2 hover:text-[color:var(--vt-orange)]" data-testid={`edit-${p.slug}`}><Pencil size={16} /></button>
                    <button onClick={() => del(p)} className="p-2 hover:text-red-600" data-testid={`delete-${p.slug}`}><Trash size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {(editing || showNew) && (
        <ProductModal
          product={editing}
          categories={categories}
          onClose={() => { setEditing(null); setShowNew(false); }}
          onSaved={load}
        />
      )}
      {showBulk && <BulkImportModal onClose={() => setShowBulk(false)} onDone={load} />}
    </div>
  );
}
